/**
 * The blocking card a fan sees when their birthday puts them under 13.
 *
 * This card IS the single confirmation for the deletion. The destructive-
 * confirmation rule carries this exemption verbatim: "The under-13 refusal
 * path is the exception, and deliberately so." Owner batch B `Q182` settled
 * that the app states the account will be deleted and deletes it fully on
 * confirmation, and that flow already has its own single confirmation and
 * must not be made harder to complete. No second dialog, no type-to-confirm.
 */
import { useAccountDelete } from "@sportsmet/auth-sdk/private";
import { Button } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useRef, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import { clearSessionTokens } from "../../auth/sessionCookies";
import { showApiError } from "../../errors/apiErrorBus";
import {
  buildDeleteRequestId,
  DELETE_ACCOUNT_LAST_SUPER_ADMIN_MESSAGE,
  LAST_SUPER_ADMIN_CODE,
} from "../account/deleteAccountCopy";
import {
  UNDER_AGE_BODY,
  UNDER_AGE_CANCEL_LABEL,
  UNDER_AGE_DELETE_LABEL,
  UNDER_AGE_DELETE_PENDING_LABEL,
  UNDER_AGE_HEADLINE,
} from "./onboardingCopy";

export const UnderAgeRefusal = ({
  onCancel,
}: {
  onCancel: () => void;
}): ReactNode => {
  const deleteAccount = useAccountDelete();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const isRunning = useRef(false);
  const [lastSuperAdminMessage, setLastSuperAdminMessage] = useState<
    string | null
  >(null);

  async function handleDelete(): Promise<void> {
    if (isRunning.current) return;
    isRunning.current = true;
    setLastSuperAdminMessage(null);

    let result;
    try {
      result = await deleteAccount.mutateAsync({
        requestId: buildDeleteRequestId(),
      });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isRunning.current = false;
      return;
    }

    if (result.error?.code === LAST_SUPER_ADMIN_CODE) {
      setLastSuperAdminMessage(DELETE_ACCOUNT_LAST_SUPER_ADMIN_MESSAGE);
      isRunning.current = false;
      return;
    }

    if (showApiError(result)) {
      isRunning.current = false;
      return;
    }

    clearSessionTokens();
    queryClient.clear();
    // signOut() would call session/revoke against an account that no longer
    // exists. Refreshing with no token is AuthProvider's local-only path.
    await refreshSession();
    void navigate({
      to: "/sign-in",
      search: { step: "email" as const, deleted: true },
      replace: true,
    });
  }

  return (
    <div
      role="alert"
      data-testid="onboarding-under-age"
      className="flex flex-col gap-1 rounded-md border border-error bg-surface p-4"
    >
      <h2 className="text-h2 text-ink">{UNDER_AGE_HEADLINE}</h2>
      <p className="text-body text-ink-secondary">{UNDER_AGE_BODY}</p>
      <div className="flex flex-col gap-2">
        <Button
          variant="destructive"
          fullWidth
          loading={deleteAccount.isPending}
          loadingLabel={UNDER_AGE_DELETE_PENDING_LABEL}
          data-testid="onboarding-under-age-delete"
          onClick={() => {
            void handleDelete();
          }}
        >
          {UNDER_AGE_DELETE_LABEL}
        </Button>
        <Button
          variant="secondary"
          fullWidth
          disabled={deleteAccount.isPending}
          data-testid="onboarding-under-age-cancel"
          onClick={onCancel}
        >
          {UNDER_AGE_CANCEL_LABEL}
        </Button>
      </div>
      {lastSuperAdminMessage === null ? null : (
        <p
          role="alert"
          className="text-body text-error"
          data-testid="onboarding-under-age-refusal"
        >
          {lastSuperAdminMessage}
        </p>
      )}
    </div>
  );
};
